import { wordAudioScope } from "./script";
import type { Lesson } from "./types";

export type KanaWordScript = "hiragana" | "katakana";
export interface KanaWord { text: string; meaning: string; image: string }
export interface KanaWordLesson { id: string; scriptType: KanaWordScript; category: string; words: KanaWord[] }
export interface KanaWordStatus { id: string; status: string; audioReady: number; activeJobs: number; failedJobs: number }

const word = (text: string, meaning: string, image: string): KanaWord => ({ text, meaning, image });
export const KANA_WORD_LESSONS: KanaWordLesson[] = [
  { id: "lesson-kana-words-hiragana-animals", scriptType: "hiragana", category: "동물", words: [
    word("ねこ", "고양이", "1f408"), word("いぬ", "개", "1f415"), word("うさぎ", "토끼", "1f407"),
    word("とり", "새", "1f426"), word("さかな", "물고기", "1f41f"), word("くま", "곰", "1f43b"), word("うま", "말", "1f40e"), word("ぶた", "돼지", "1f416")
  ] },
  { id: "lesson-kana-words-hiragana-food", scriptType: "hiragana", category: "음식", words: [
    word("りんご", "사과", "1f34e"), word("みかん", "귤", "1f34a"), word("たまご", "달걀", "1f95a"),
    word("おにぎり", "주먹밥", "1f359"), word("やさい", "채소", "1f966"), word("いちご", "딸기", "1f353"), word("ごはん", "밥", "1f35a"), word("みず", "물", "1f6b0")
  ] },
  { id: "lesson-kana-words-hiragana-body", scriptType: "hiragana", category: "우리 몸", words: [
    word("て", "손", "270b"), word("あし", "발", "1f9b6"), word("め", "눈", "1f441"),
    word("みみ", "귀", "1f442"), word("くち", "입", "1f444"), word("はな", "코", "1f443"), word("あたま", "머리", "1f9d1"), word("かお", "얼굴", "1f642")
  ] },
  { id: "lesson-kana-words-hiragana-family", scriptType: "hiragana", category: "가족", words: [
    word("おかあさん", "어머니", "1f469"), word("おとうさん", "아버지", "1f468"), word("おにいさん", "형·오빠", "1f466"), word("おねえさん", "누나·언니", "1f467"),
    word("おとうと", "남동생", "1f476"), word("いもうと", "여동생", "1f476"), word("おばあさん", "할머니", "1f475"), word("おじいさん", "할아버지", "1f474")
  ] },
  { id: "lesson-kana-words-hiragana-nature", scriptType: "hiragana", category: "자연", words: [
    word("やま", "산", "26f0"), word("かわ", "강", "1f3de"), word("うみ", "바다", "1f30a"), word("そら", "하늘", "1f324"),
    word("くも", "구름", "2601"), word("あめ", "비", "1f327"), word("ゆき", "눈", "2744"), word("ほし", "별", "2b50")
  ] },
  { id: "lesson-kana-words-hiragana-home", scriptType: "hiragana", category: "집", words: [
    word("いえ", "집", "1f3e0"), word("へや", "방", "1f6cb"), word("まど", "창문", "1fa9f"), word("ゆか", "바닥", "floor"),
    word("かべ", "벽", "1f9f1"), word("にわ", "정원", "1f3e1"), word("かぎ", "열쇠", "1f511"), word("でんき", "전기", "1f4a1")
  ] },
  { id: "lesson-kana-words-hiragana-actions", scriptType: "hiragana", category: "행동", words: [
    word("あるく", "걷다", "1f6b6"), word("はしる", "달리다", "1f3c3"), word("たべる", "먹다", "1f37d"), word("のむ", "마시다", "1f964"),
    word("ねる", "자다", "1f6cc"), word("みる", "보다", "1f440"), word("きく", "듣다", "1f442"), word("かく", "쓰다", "270d")
  ] },
  { id: "lesson-kana-words-hiragana-feelings", scriptType: "hiragana", category: "기분과 느낌", words: [
    word("うれしい", "기쁘다", "1f60a"), word("かなしい", "슬프다", "1f622"), word("たのしい", "즐겁다", "1f604"), word("こわい", "무섭다", "1f628"),
    word("ねむい", "졸리다", "1f634"), word("いたい", "아프다", "1f915"), word("さむい", "춥다", "1f976"), word("あつい", "덥다", "1f975")
  ] },
  { id: "lesson-kana-words-hiragana-colors", scriptType: "hiragana", category: "색깔", words: [
    word("あか", "빨강", "1f534"), word("あお", "파랑", "1f535"), word("きいろ", "노랑", "1f7e1"), word("みどり", "초록", "1f7e2"),
    word("しろ", "하양", "26aa"), word("くろ", "검정", "26ab"), word("むらさき", "보라", "1f7e3"), word("ちゃいろ", "갈색", "1f7e4")
  ] },
  { id: "lesson-kana-words-hiragana-time", scriptType: "hiragana", category: "시간", words: [
    word("あさ", "아침", "1f305"), word("ひる", "낮", "2600"), word("よる", "밤", "1f303"), word("きょう", "오늘", "1f4c5"),
    word("きのう", "어제", "1f519"), word("あした", "내일", "1f51c"), word("いま", "지금", "23f0"), word("まいにち", "매일", "1f4c6")
  ] },
  { id: "lesson-kana-words-katakana-animals", scriptType: "katakana", category: "동물", words: [
    word("ライオン", "사자", "1f981"), word("キリン", "기린", "1f992"), word("パンダ", "판다", "1f43c"),
    word("コアラ", "코알라", "1f428"), word("ゴリラ", "고릴라", "1f98d"), word("ペンギン", "펭귄", "1f427"), word("カンガルー", "캥거루", "1f998"), word("シマウマ", "얼룩말", "1f993")
  ] },
  { id: "lesson-kana-words-katakana-food", scriptType: "katakana", category: "음식", words: [
    word("パン", "빵", "1f35e"), word("ケーキ", "케이크", "1f370"), word("アイス", "아이스크림", "1f368"),
    word("チーズ", "치즈", "1f9c0"), word("スープ", "수프", "1f963"), word("ジュース", "주스", "1f9c3"), word("チョコレート", "초콜릿", "1f36b"), word("ヨーグルト", "요구르트", "yogurt")
  ] },
  { id: "lesson-kana-words-katakana-objects", scriptType: "katakana", category: "생활 물건", words: [
    word("カメラ", "카메라", "1f4f7"), word("テレビ", "텔레비전", "1f4fa"), word("ペン", "펜", "1f58a"),
    word("ノート", "공책", "1f4d3"), word("ソファ", "소파", "1f6cb"), word("ベッド", "침대", "1f6cf"), word("ランプ", "램프", "1f4a1"), word("テーブル", "탁자", "table")
  ] },
  { id: "lesson-kana-words-katakana-transport", scriptType: "katakana", category: "탈것", words: [
    word("バス", "버스", "1f68c"), word("タクシー", "택시", "1f695"), word("トラック", "트럭", "1f69a"), word("トラクター", "트랙터", "1f69c"),
    word("バイク", "오토바이", "1f3cd"), word("ヘリコプター", "헬리콥터", "1f681"), word("ロケット", "로켓", "1f680"), word("ボート", "보트", "1f6a4")
  ] },
  { id: "lesson-kana-words-katakana-clothes", scriptType: "katakana", category: "옷", words: [
    word("シャツ", "셔츠", "1f455"), word("ズボン", "바지", "1f456"), word("スカート", "치마", "skirt"), word("コート", "코트", "1f9e5"),
    word("セーター", "스웨터", "sweater"), word("ジャケット", "재킷", "1f9e5"), word("マフラー", "목도리", "1f9e3"), word("ブーツ", "부츠", "1f462")
  ] },
  { id: "lesson-kana-words-katakana-sports", scriptType: "katakana", category: "운동", words: [
    word("サッカー", "축구", "26bd"), word("テニス", "테니스", "1f3be"), word("バスケットボール", "농구", "1f3c0"), word("バレーボール", "배구", "1f3d0"),
    word("ゴルフ", "골프", "26f3"), word("スキー", "스키", "1f3bf"), word("スケート", "스케이트", "26f8"), word("サーフィン", "서핑", "1f3c4")
  ] },
  { id: "lesson-kana-words-katakana-instruments", scriptType: "katakana", category: "악기", words: [
    word("ピアノ", "피아노", "1f3b9"), word("ギター", "기타", "1f3b8"), word("ドラム", "드럼", "1f941"), word("バイオリン", "바이올린", "1f3bb"),
    word("フルート", "플루트", "1fa88"), word("トランペット", "트럼펫", "1f3ba"), word("サックス", "색소폰", "1f3b7"), word("タンバリン", "탬버린", "tambourine")
  ] },
  { id: "lesson-kana-words-katakana-places", scriptType: "katakana", category: "장소", words: [
    word("ホテル", "호텔", "1f3e8"), word("レストラン", "식당", "1f37d"), word("カフェ", "카페", "2615"), word("スーパー", "슈퍼마켓", "1f6d2"),
    word("コンビニ", "편의점", "1f3ea"), word("デパート", "백화점", "1f3ec"), word("プール", "수영장", "1f3ca"), word("トイレ", "화장실", "1f6bd")
  ] },
  { id: "lesson-kana-words-katakana-fruits", scriptType: "katakana", category: "과일", words: [
    word("バナナ", "바나나", "1f34c"), word("メロン", "멜론", "1f348"), word("レモン", "레몬", "1f34b"), word("オレンジ", "오렌지", "1f34a"),
    word("パイナップル", "파인애플", "1f34d"), word("マンゴー", "망고", "1f96d"), word("キウイ", "키위", "1f95d"), word("ココナッツ", "코코넛", "1f965")
  ] },
  { id: "lesson-kana-words-katakana-digital", scriptType: "katakana", category: "디지털 기기", words: [
    word("パソコン", "컴퓨터", "1f4bb"), word("タブレット", "태블릿", "tablet"), word("スマートフォン", "스마트폰", "1f4f1"), word("キーボード", "키보드", "2328"),
    word("マウス", "마우스", "1f5b1"), word("モニター", "모니터", "1f5a5"), word("プリンター", "프린터", "1f5a8"), word("メモリーカード", "메모리 카드", "memory-card")
  ] }
];

export function kanaWordLesson(id: string): KanaWordLesson | undefined {
  return KANA_WORD_LESSONS.find((plan) => plan.id === id);
}

export function kanaWordAudioReady(lesson: Lesson, plan: KanaWordLesson): boolean {
  return plan.words.every((item) => lesson.assets.some((asset) => asset.scope === wordAudioScope(item.text)
    && asset.asset.assetType === "audio" && asset.asset.qualityStatus === "approved"));
}
